import React from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { Amplify } from "aws-amplify";
import { MantineProvider } from "@mantine/core";
import { Authenticator, useAuthenticator } from "@aws-amplify/ui-react";
import { Userpilot } from "userpilot";
import { usePostHog } from "posthog-js/react";
import { AutoLogout } from "shared/src/components/auto-logout";
import { AuthEvent } from "./data/auth-events";
import ProductLayout from "./components/product/layout/layout";
import productRoutes from "./routes/product-routes";
import productEligibilityRoutes from "./routes/product-eligibility-routes";
import accountRoutes from "./routes/account-routes";
import routesWithoutSignup from "./routes/routes-without-signup";
import appealManagementRoutes from "./routes/appeal-management-routes";
import QueryAuthGate from "./routes/query-auth-gate/query-auth-gate";
import { EligibilityProvider } from "./contexts/eligibility-context";
import { AppealIdProvider } from "./contexts/appeal-id-context";
import { PatientProvider } from "./contexts/patient-context";
import { RequireAppealId } from "./components/shared/require-appeal-id";
import ClaimableAuthenticatorV2 from "./components/shared/claimable-authenticator-v2";
import Page from "./components/product/layout/page";
import theme from "./theme";
import IdentifyUser from "./components/identify-user";
import AuthRouteTracker from "./components/auth-route-tracker";
import PathConstants from "./routes/path-constants";
import { MetaProvider } from "./contexts/meta-context";
import { InitialAppealProvider } from "./contexts/initial-appeal-context";
import AppealManagementAuthGate from "./routes/appeal-management-auth-gate/appeal-management-auth-gate";

import "@aws-amplify/ui-react/styles.css";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_USER_POOL_ID,
      userPoolClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID,
      identityPoolId: import.meta.env.VITE_IDENTITY_POOL_ID,
      allowGuestAccess: true,
    },
  },
});

Userpilot.initialize("NX-32bc6362");

const ResumeAppeal = React.lazy(
  () => import("./pages/product/resume/resume-appeal"),
);

const Appeals = React.lazy(() => import("./pages/product/account/appeals"));
const ReportAppealResultPage = React.lazy(
  () => import("./pages/product/reporting-flow/report-appeal-result"),
);
const UpdateAppealStatus = React.lazy(
  () => import("./pages/update-appeal-status"),
);
const MagicLinkUpheldFollowUp = React.lazy(
  () => import("./pages/magic-link-upheld-follow-up"),
);
function AutoLogoutWrapper() {
  const { signOut, authStatus } = useAuthenticator();
  const posthog = usePostHog();

  const handleLogout = () => {
    localStorage.clear();
    signOut();
  };

  const handleInactivityLogout = () => {
    posthog.capture(AuthEvent.InactivityLogout);
  };

  if (authStatus !== "authenticated") {
    return null;
  }

  return (
    <AutoLogout
      onLogout={handleLogout}
      onInactivityLogout={handleInactivityLogout}
    />
  );
}

function App() {
  const router = createBrowserRouter([
    {
      element: (
        <ClaimableAuthenticatorV2>
          <RequireAppealId>
            <ProductLayout />
          </RequireAppealId>
        </ClaimableAuthenticatorV2>
      ),
      errorElement: <Page errorLoading heading={false} menuButton={false} />,
      children: productRoutes,
    },
    {
      element: <ProductLayout />,
      errorElement: <Page errorLoading heading={false} menuButton={false} />,
      children: productEligibilityRoutes,
    },
    {
      element: (
        <ClaimableAuthenticatorV2 defaultTab="signIn">
          <ProductLayout />
        </ClaimableAuthenticatorV2>
      ),
      errorElement: <Page errorLoading heading={false} menuButton={false} />,
      children: accountRoutes,
    },
    {
      element: (
        <ClaimableAuthenticatorV2 defaultTab="signIn">
          <ProductLayout />
        </ClaimableAuthenticatorV2>
      ),
      errorElement: <Page errorLoading heading={false} menuButton={false} />,
      children: [
        {
          path: PathConstants.MagicLinkUpheldFollowUp,
          element: <MagicLinkUpheldFollowUp />,
        },
      ],
    },
    {
      element: (
        <ClaimableAuthenticatorV2 defaultTab="signIn" hideSignUp>
          <ProductLayout />
        </ClaimableAuthenticatorV2>
      ),
      errorElement: <Page errorLoading heading={false} menuButton={false} />,
      children: routesWithoutSignup,
    },
    {
      element: <AppealManagementAuthGate />,
      errorElement: <Page errorLoading heading={false} menuButton={false} />,
      children: appealManagementRoutes,
    },
    {
      element: <ProductLayout />,
      errorElement: <Page errorLoading heading={false} menuButton={false} />,
      children: [
        { path: PathConstants.ResumeAppeal, element: <ResumeAppeal /> },
        {
          path: PathConstants.Appeals,
          element: (
            <QueryAuthGate>
              <Appeals />
            </QueryAuthGate>
          ),
        },
        {
          path: PathConstants.ReportAppealResult,
          element: (
            <QueryAuthGate>
              <ReportAppealResultPage />
            </QueryAuthGate>
          ),
        },
      ],
    },
    {
      element: <ProductLayout />,
      errorElement: <Page errorLoading heading={false} menuButton={false} />,
      children: [
        {
          path: PathConstants.UpdateAppealStatus,
          element: <UpdateAppealStatus />,
        },
      ],
    },
  ]);

  return (
    <MantineProvider theme={theme}>
      <MetaProvider>
        <Authenticator.Provider>
          <AppealIdProvider>
            <PatientProvider>
              <InitialAppealProvider>
                <EligibilityProvider>
                  <RouterProvider router={router} />
                </EligibilityProvider>
              </InitialAppealProvider>
            </PatientProvider>
          </AppealIdProvider>
          <IdentifyUser />
          <AuthRouteTracker />
          <AutoLogoutWrapper />
        </Authenticator.Provider>
      </MetaProvider>
    </MantineProvider>
  );
}

export default App;
